import React, { useEffect, useRef, useState } from 'react';

import { aiChatService } from '../services/aiChatService';

import './AICoach.css';

const INITIAL_ASSISTANT_MESSAGE = {
  role: 'assistant',
  content:
    'Привет! Я AIRNI — твой персональный AI‑тренер. Готов помочь с тренировками, питанием и мотивацией. О чем хочешь поговорить?',
  timestamp: new Date()
};

const MAX_HISTORY_MESSAGES = 20;
const MAX_SAVED_MESSAGES = 10;
const CHAT_STORAGE_KEY = 'airni_chat_history';

// Функция для загрузки истории из localStorage
const loadChatHistory = () => {
  try {
    const savedHistory = localStorage.getItem(CHAT_STORAGE_KEY);
    if (savedHistory) {
      const parsed = JSON.parse(savedHistory);
      // Восстанавливаем Date объекты из строк
      return parsed.map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));
    }
  } catch (error) {
    console.error('Error loading chat history:', error);
  }
  return [INITIAL_ASSISTANT_MESSAGE];
};

// Функция для сохранения истории в localStorage
const saveChatHistory = (messages) => {
  try {
    // Сохраняем только последние 10 сообщений
    const messagesToSave = messages.slice(-MAX_SAVED_MESSAGES);
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messagesToSave));
  } catch (error) {
    console.error('Error saving chat history:', error);
  }
};

const AICoach = () => {
  const [messages, setMessages] = useState(loadChatHistory);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Сохраняем историю чата при каждом изменении сообщений
  useEffect(() => {
    saveChatHistory(messages);
  }, [messages]);

  useEffect(() => {
    if (!textareaRef.current) {
      return;
    }
    const el = textareaRef.current;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 100)}px`;
  }, [inputMessage]);

  const buildHistoryPayload = () =>
    messages
      .filter((msg) => msg.role === 'user' || msg.role === 'assistant')
      .slice(-MAX_HISTORY_MESSAGES)
      .map((msg) => ({
        role: msg.role,
        content: msg.content
      }));

  const handleSendMessage = async (messageText = inputMessage) => {
    const trimmedMessage = messageText.trim();
    if (!trimmedMessage || isLoading) {
      return;
    }

    const userMessage = {
      role: 'user',
      content: trimmedMessage,
      timestamp: new Date()
    };

    const historyPayload = buildHistoryPayload();

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const { response } = await aiChatService.sendMessage(trimmedMessage, historyPayload);

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: response,
          timestamp: new Date()
        }
      ]);
    } catch (error) {
      console.error('AI chat error:', error);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            '⚠️ Не удалось связаться с AI. Попробуй еще раз чуть позже или проверь подключение.',
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="ai-coach">
      <div className="coach-container">
        <div className="coach-header">
          <div className="coach-header-text">
            <div className="coach-header-title">
              <span className="coach-header-icon">🤖</span>
              <h2>AIRNI</h2>
            </div>
            <p className="coach-header-subtitle">Персональный тренер</p>
          </div>
        </div>

        <div className="messages-container">
          {messages.map((message, index) => (
            <div key={index} className={`message ${message.role}`}>
              <div className="message-content">{message.content}</div>
            </div>
          ))}
          {isLoading && (
            <div className="message assistant">
              <div className="message-content typing-indicator">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-container">
          <div className="chat-input">
            <textarea
              value={inputMessage}
              onChange={(event) => setInputMessage(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder="Спросите что-нибудь"
              disabled={isLoading}
              ref={textareaRef}
            />
            <button
              className="send-button"
              onClick={() => handleSendMessage()}
              disabled={isLoading || !inputMessage.trim()}
              aria-label="Отправить"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AICoach;
